import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _jsxFileName = "/home/bamboo/bamboo-agent-home/xml-data/build-dir/DLS-WEB7-JOB1/src/components/Map/Map.js",
  _this = this;
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { render } from 'react-dom';
import { useLoadScript, GoogleMap } from '@react-google-maps/api';
import styled from 'styled-components';
import Spacing from '../Spacing';
import { useTheme, withTheme } from '../../core/ThemeProvider';
import useDevice from '../../core/hooks/useDevice';
import { getMapStyles, StyledMapContainer } from './styles';
import LocationDetailsCard from './LocationDetailsCard';
import Marker from './Marker';
import { getLocationsWithDistanceFromPoint, getUniqueCategories, getCalculatedMapHeight, getLocationIndex } from './helpers';
import FilterModal from './FilterModal';
import SearchResults from './SearchResults';
import SearchInput from './SearchInput';
import { errorMessages, locationDetailsCardClassName, markerLabelClassName, responseStatus, singaporeCountryCode } from './config';
var StyledContainer = withTheme(styled.div.withConfig({
  displayName: "Map__StyledContainer",
  componentId: "sc-1m393np-0"
})(["display:flex;flex-direction:column;width:100%;.", "{text-align:center;line-height:", ";letter-spacing:", ";width:104px;white-space:normal;text-shadow:", ";}"], markerLabelClassName, function (p) {
  return p.coreTheme.map.marker.labelLineHeight;
}, function (p) {
  return p.coreTheme.map.marker.labelLetterSpacing;
}, function (p) {
  return p.coreTheme.map.marker.labelTextShadow;
}));
export var Map = function Map(_ref) {
  var _ref$locations = _ref.locations,
    locations = _ref$locations === void 0 ? [] : _ref$locations,
    searchConfig = _ref.searchConfig,
    filterConfig = _ref.filterConfig,
    locationDetailsConfig = _ref.locationDetailsConfig,
    defaultCenterCoordinates = _ref.defaultCenterCoordinates,
    _ref$zoomConfig = _ref.zoomConfig,
    zoomConfig = _ref$zoomConfig === void 0 ? {} : _ref$zoomConfig,
    onLocationSelected = _ref.onLocationSelected,
    googleMapsApiKey = _ref.googleMapsApiKey,
    _ref$showMapWithoutRe = _ref.showMapWithoutResults,
    showMapWithoutResults = _ref$showMapWithoutRe === void 0 ? true : _ref$showMapWithoutRe,
    _ref$searchRequiredEr = _ref.searchRequiredError,
    searchRequiredError = _ref$searchRequiredEr === void 0 ? '' : _ref$searchRequiredEr,
    _ref$onLocationSearch = _ref.onLocationSearchError,
    onLocationSearchError = _ref$onLocationSearch === void 0 ? function () {
      return null;
    } : _ref$onLocationSearch,
    preSelection = _ref.preSelection,
    _ref$showMarkersWitho = _ref.showMarkersWithoutSearch,
    showMarkersWithoutSearch = _ref$showMarkersWitho === void 0 ? true : _ref$showMarkersWitho;
  var _zoomConfig$defaultZo = zoomConfig.defaultZoom,
    defaultZoom = _zoomConfig$defaultZo === void 0 ? 12 : _zoomConfig$defaultZo,
    _zoomConfig$focusedAr = zoomConfig.focusedAreaZoom,
    focusedAreaZoom = _zoomConfig$focusedAr === void 0 ? 15 : _zoomConfig$focusedAr,
    _zoomConfig$labelHidi = zoomConfig.labelHidingZoom,
    labelHidingZoom = _zoomConfig$labelHidi === void 0 ? 14 : _zoomConfig$labelHidi;
  var _useLoadScript = useLoadScript({
      googleMapsApiKey: googleMapsApiKey
    }),
    isLoaded = _useLoadScript.isLoaded;
  var _useTheme = useTheme(),
    theme = _useTheme.theme;
  var _useDevice = useDevice(),
    isMobile = _useDevice.isMobile;
  var _useState = useState(searchRequiredError),
    _useState2 = _slicedToArray(_useState, 2),
    searchError = _useState2[0],
    setSearchError = _useState2[1];
  var _useState3 = useState(preSelection),
    _useState4 = _slicedToArray(_useState3, 2),
    preLocationSelection = _useState4[0],
    setPreLocationSelection = _useState4[1];
  var _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    searchedTerm = _useState6[0],
    setSearchedTerm = _useState6[1];
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    searchedLocation = _useState8[0],
    setSearchedLocation = _useState8[1];
  var _useState9 = useState(100),
    _useState10 = _slicedToArray(_useState9, 2),
    mapHeight = _useState10[0],
    setMapHeight = _useState10[1];
  var _useState11 = useState(defaultZoom),
    _useState12 = _slicedToArray(_useState11, 2),
    zoom = _useState12[0],
    setZoom = _useState12[1];
  var _useState13 = useState([]),
    _useState14 = _slicedToArray(_useState13, 2),
    categories = _useState14[0],
    setCategories = _useState14[1];
  var _useState15 = useState(false),
    _useState16 = _slicedToArray(_useState15, 2),
    showFilterModal = _useState16[0],
    setShowFilterModal = _useState16[1];
  var _useState17 = useState(locations),
    _useState18 = _slicedToArray(_useState17, 2),
    markersData = _useState18[0],
    setMarkersData = _useState18[1];
  var _useState19 = useState(true),
    _useState20 = _slicedToArray(_useState19, 2),
    showMapContainer = _useState20[0],
    setShowMapContainer = _useState20[1];
  var mapContainerRef = useRef();
  var mapRef = useRef();
  var geocoderRef = useRef(null);
  var setMapHeightValue = function setMapHeightValue(isLoaded) {
    var widthRatio = 16;
    var heightRatio = 9;
    var showMapContainerValue = !!searchedLocation || showMapWithoutResults;
    setShowMapContainer(showMapContainerValue);
    if (isLoaded && mapContainerRef.current) {
      var mapWidth = mapContainerRef.current.offsetWidth;
      var _mapHeight = showMapContainerValue ? getCalculatedMapHeight(mapWidth, widthRatio, heightRatio) : 0;
      setMapHeight(_mapHeight);
    }
  };
  useEffect(function () {
    setMapHeightValue(isLoaded);
    var handleResize = function handleResize() {
      setMapHeightValue(isLoaded);
    };
    window.addEventListener('resize', handleResize);
    return function () {
      window.removeEventListener('resize', handleResize);
    };
  }, [isLoaded, searchedLocation]);
  var mapOptions = useMemo(function () {
    return {
      mapTypeControl: false,
      streetViewControl: false,
      zoomControl: !isMobile,
      styles: getMapStyles(theme)
    };
  }, [isMobile, theme]);
  var mapContainerStyle = useMemo(function () {
    return {
      height: "".concat(mapHeight, "px")
    };
  }, [mapHeight]);
  var onLoad = useCallback(function (map) {
    mapRef.current = map;
    geocoderRef.current = new window.google.maps.Geocoder();
    var locationDetailsCardElem = document.createElement('div');
    locationDetailsCardElem.setAttribute('class', locationDetailsCardClassName);
    mapRef.current.getDiv().firstChild.appendChild(locationDetailsCardElem);
    if (geocoderRef.current && preLocationSelection) {
      onLocationSearch(preLocationSelection.searchTerm);
    }
  }, []);
  var onMapZoomChanged = useCallback(function () {
    if (mapRef.current && mapRef.current.zoom) {
      setZoom(mapRef.current.zoom);
    }
  }, []);
  var onMapBoundsChanged = useCallback(function () {
    var mapFirstChild = mapRef.current.getDiv().firstChild;
    var locationDetailsCard = mapRef.current.getDiv().firstChild.getElementsByClassName(locationDetailsCardClassName)[0];
    if (mapFirstChild.clientHeight === window.innerHeight && mapFirstChild.clientWidth === window.innerWidth) {
      locationDetailsCard.style.display = 'block';
    } else {
      locationDetailsCard.style.display = 'none';
    }
  }, []);
  useEffect(function () {
    var markers = locations.map(function (point) {
      return Object.assign({}, point, {
        isSelected: false
      });
    });
    setMarkersData(markers);
  }, [locations]);
  useEffect(function () {
    setShowMapContainer(!!searchedLocation || showMapWithoutResults);
  }, [searchedLocation, showMapWithoutResults]);
  useEffect(function () {
    onLocationSearchError(searchError);
  }, [searchError]);
  useEffect(function () {
    setSearchError(searchRequiredError);
  }, [searchRequiredError]);
  var handleMarkerSelection = function handleMarkerSelection() {
    var markersDataList = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    var index = arguments.length > 1 ? arguments[1] : undefined;
    var searchTerm = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
    var markers = markersDataList.map(function (markerData, i) {
      return Object.assign({}, markerData, {
        isSelected: i === index
      });
    });
    setMarkersData(markers);
    var selectedMarker = markers[index];
    mapRef.current.panTo({
      lng: selectedMarker.lng,
      lat: selectedMarker.lat
    });
    if (zoom < focusedAreaZoom) {
      setZoom(focusedAreaZoom);
    }
    render( /*#__PURE__*/React.createElement(LocationDetailsCard, {
      name: selectedMarker.name,
      descriptionLiner: selectedMarker.descriptionLiner,
      subDescriptionLiner: selectedMarker.subDescriptionLiner,
      postalCode: selectedMarker.postalCode,
      directionsLinkText: locationDetailsConfig && locationDetailsConfig.directionsLinkText,
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 223,
        columnNumber: 7
      }
    }), mapRef.current.getDiv().firstChild.getElementsByClassName(locationDetailsCardClassName)[0]);
    if (typeof onLocationSelected === 'function') {
      onLocationSelected(Object.assign({}, selectedMarker, {
        searchTerm: searchTerm || searchedTerm
      }));
    }
  };
  var resetMarkerSelection = function resetMarkerSelection() {
    var markers = locations.map(function (point) {
      return Object.assign({}, point, {
        isSelected: false
      });
    });
    setMarkersData(markers);
  };
  var onLocationSearch = function onLocationSearch(searchTerm) {
    setSearchedLocation(null);
    if (searchTerm.length != 6) {
      setSearchError(searchConfig.invalidPostalCodeLengthError || errorMessages.invalidPostalCodeLength);
      resetMarkerSelection();
      return;
    }
    setSearchedTerm(searchTerm);
    geocoderRef.current.geocode({
      componentRestrictions: {
        country: singaporeCountryCode,
        postalCode: searchTerm
      }
    }, function (results, status) {
      if (status == responseStatus.OK) {
        var _results$0$geometry$l = results[0].geometry.location,
          lat = _results$0$geometry$l.lat,
          lng = _results$0$geometry$l.lng;
        var searchedPosition = {
          lat: lat(),
          lng: lng()
        };
        var locationsWithDistance = getLocationsWithDistanceFromPoint(locations, searchedPosition, searchConfig.searchRadius);
        if (locationsWithDistance && locationsWithDistance.length > 0) {
          mapRef.current.panTo({
            lng: locationsWithDistance[0].lng,
            lat: locationsWithDistance[0].lat
          });
        }
        var locationIndex = getLocationIndex(preLocationSelection, locationsWithDistance);
        handleMarkerSelection(locationsWithDistance, locationIndex, searchTerm);
        var uniqueCategories = getUniqueCategories(locationsWithDistance);
        var _categories = [];
        uniqueCategories.forEach(function (uniqueCategory) {
          _categories.push(Object.assign({}, uniqueCategory, {
            selected: true
          }));
        });
        setCategories(_categories);
        setSearchError('');
        setSearchedLocation(searchedPosition);
      } else {
        setSearchError(searchConfig.invalidPostalCodeError || errorMessages.invalidPostalCode);
        resetMarkerSelection();
      }
      setPreLocationSelection(null);
    });
  };
  var onFilterClick = function onFilterClick() {
    setShowFilterModal(true);
  };
  var onFilterModalClose = function onFilterModalClose() {
    setShowFilterModal(false);
  };
  var onFiltersUpdated = function onFiltersUpdated(filterOptions) {
    setCategories(filterOptions);
    var selectedFilters = filterOptions.filter(function (opt) {
      return opt.selected;
    }).map(function (opt) {
      return opt.value;
    });
    var locationsWithDistance = getLocationsWithDistanceFromPoint(locations, searchedLocation, searchConfig.searchRadius);
    var filteredLocations = locationsWithDistance.filter(function (location) {
      if (location.categories) {
        var locationCategories = location.categories.map(function (cat) {
          return cat.value;
        });
        var matchingCategories = locationCategories.filter(function (item) {
          return selectedFilters.includes(item);
        });
        return matchingCategories.length > 0;
      }
      return true;
    });
    handleMarkerSelection(filteredLocations, 0);
    mapRef.current.panTo({
      lng: filteredLocations[0].lng,
      lat: filteredLocations[0].lat
    });
    setShowFilterModal(false);
  };
  var renderSearchResultsHeading = function renderSearchResultsHeading() {
    var selectedFilters = categories.filter(function (opt) {
      return opt.selected;
    }).map(function (opt) {
      return opt.name;
    });
    return typeof searchConfig.renderSearchResultsHeading === 'function' && searchConfig.renderSearchResultsHeading(markersData.length, selectedFilters, searchedTerm);
  };
  if (!isLoaded) return /*#__PURE__*/React.createElement("div", {
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 385,
      columnNumber: 25
    }
  }, "Loading...");
  var showMarkersOnLoad = showMarkersWithoutSearch || !showMarkersWithoutSearch && !!searchedTerm;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledContainer, {
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 392,
      columnNumber: 7
    }
  }, searchConfig && /*#__PURE__*/React.createElement(Spacing, {
    bottom: 2,
    responsive: false,
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 394,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(SearchInput, {
    preSearchTerm: preSelection === null || preSelection === void 0 ? void 0 : preSelection.searchTerm,
    label: searchConfig.searchInputLabel,
    errorMessage: searchError,
    onSearch: function onSearch(searchTerm) {
      onLocationSearch(searchTerm);
    },
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 395,
      columnNumber: 13
    }
  })), /*#__PURE__*/React.createElement(StyledMapContainer, {
    show: showMapContainer,
    ref: mapContainerRef,
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 406,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(GoogleMap, {
    zoom: zoom,
    mapContainerStyle: mapContainerStyle,
    options: mapOptions,
    onLoad: onLoad,
    onZoomChanged: onMapZoomChanged,
    onBoundsChanged: onMapBoundsChanged,
    center: defaultCenterCoordinates,
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 407,
      columnNumber: 11
    }
  }, showMarkersOnLoad && /*#__PURE__*/React.createElement(React.Fragment, null, markersData.map(function (markerData, index) {
    return /*#__PURE__*/React.createElement(Marker, {
      markerData: markerData,
      key: index,
      onClick: function onClick() {
        handleMarkerSelection(markersData, index);
      },
      showLabel: zoom >= labelHidingZoom,
      markerLabelClassName: markerLabelClassName,
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 419,
        columnNumber: 19
      }
    });
  })))), showFilterModal && /*#__PURE__*/React.createElement(FilterModal, {
    filters: categories,
    title: filterConfig.modalTitle,
    buttonLabel: filterConfig.modalButtonLabel,
    onFilterModalClose: onFilterModalClose,
    onFiltersUpdated: onFiltersUpdated,
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 435,
      columnNumber: 11
    }
  }), searchedLocation && /*#__PURE__*/React.createElement(SearchResults, {
    showFilter: !!filterConfig,
    onFilterClick: onFilterClick,
    locations: markersData,
    onLocationSelected: function onLocationSelected(index) {
      handleMarkerSelection(markersData, index);
    },
    renderHeading: renderSearchResultsHeading,
    viewMoreLabel: searchConfig.viewMoreResultsLabel,
    viewLessLabel: searchConfig.viewLessResultsLabel,
    initialSearchResultCount: searchConfig.initialSearchResultCount,
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 445,
      columnNumber: 11
    }
  })));
};
Map.defaultProps = {};
export default Map;